import { useMemo, useState } from 'react';
import { Mail, ArrowRightLeft, AlertTriangle, QrCode, Copy, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { isValidEmail } from '@/lib';

import { useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store/root-state';
import { selectPropertyHandoverState, selectPropertyHandoverQrPayload } from '@/features/properties/propertiesSlice';
import { PropertyHandoverQr } from './components/shared/PropertyHandoverQr';
import type { PropertyStatus } from '@/domain/entities/property';

export type InitiateHandoverInvitePayload = {
  buyerEmail: string;
  note?: string;
};

export type InitiateHandoverQrPayload = {
  buyerEmail?: string;
  note?: string;
};

export function InitiateHandoverModal(props: {
  open: boolean;
  propertyStatus?: PropertyStatus;
  onOpenChange: (open: boolean) => void;

  propertyTitle?: string;
  propertyId?: string;

  onSubmitInvite: (payload: InitiateHandoverInvitePayload) => Promise<void>;
  onGenerateQr: (payload?: InitiateHandoverQrPayload) => Promise<void>;
}) {
  const { open, onOpenChange, propertyTitle, propertyId, onSubmitInvite, onGenerateQr, propertyStatus } = props;

  const [activeTab, setActiveTab] = useState<'qr' | 'invite'>('qr');

  // shared
  const [note, setNote] = useState('');

  // invite
  const [buyerEmail, setBuyerEmail] = useState('');
  const [submittingInvite, setSubmittingInvite] = useState(false);

  // local-only UX state (copy)
  const [copied, setCopied] = useState(false);

  // -----------------------------
  // ✅ Pull handover state from store by propertyId
  // -----------------------------
  const handoverState = useAppSelector((s: RootState) => selectPropertyHandoverState(s, propertyId));
  const storeQrPayload = useAppSelector((s: RootState) => selectPropertyHandoverQrPayload(s, propertyId));
  const qrPayload = propertyStatus === 'handoverInitiated' ? storeQrPayload : null;

  // If you want more than payload (code/expiry), grab full response:
  const handoverCode = handoverState.data?.handover?.handoverCode ?? null;
  const expiresAt = handoverState.data?.handover?.expiresAt ?? null;

  const generatingQr = handoverState.initiating; // store-driven
  const error = handoverState.error; // store-driven

  const emailOk = useMemo(() => isValidEmail(buyerEmail), [buyerEmail]);
  const canSubmitInvite = emailOk && !submittingInvite;

  const close = () => onOpenChange(false);

  const reset = () => {
    setActiveTab('qr');
    setBuyerEmail('');
    setNote('');
    setSubmittingInvite(false);
    setCopied(false);
    // ✅ do not reset store here; store is per-property and should persist until overwritten
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleSubmitInvite = async () => {
    const email = buyerEmail.trim();
    if (!isValidEmail(email)) return;

    try {
      setSubmittingInvite(true);
      await onSubmitInvite({
        buyerEmail: email,
        note: note.trim() ? note.trim() : undefined,
      });
      handleClose(false);
    } catch {
      setSubmittingInvite(false);
    }
  };

  const handleGenerateQr = async () => {
    setCopied(false);
    // No local error handling; store handles error
    await onGenerateQr({
      note: note.trim() ? note.trim() : undefined,
      // you can later include buyerEmail if you want to “bind” it:
      // buyerEmail: buyerEmail.trim() ? buyerEmail.trim().toLowerCase() : undefined,
    });
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  const headerDesc =
    activeTab === 'qr'
      ? 'Generate a QR code the buyer can scan in the mobile app to claim the transfer.'
      : 'Send an invitation to the buyer. They will claim the transfer in the mobile app.';

  const busy = submittingInvite || generatingQr;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[calc(100vh-24px)] w-[calc(100vw-24px)] max-w-[620px] overflow-y-auto p-0">
        <div className="p-6">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="text-muted-foreground h-5 w-5" />
              <DialogTitle>Initiate ownership handover</DialogTitle>
            </div>
            <DialogDescription>{headerDesc}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Context row */}
            <div className="bg-muted/30 rounded-lg border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">Property</Badge>
                <div className="text-sm font-medium">{propertyTitle || '—'}</div>
                {propertyId ? <div className="text-muted-foreground text-xs">({propertyId})</div> : null}
              </div>
              <div className="text-muted-foreground mt-2 text-xs">
                The buyer will be able to <span className="font-medium">accept</span> or{' '}
                <span className="font-medium">decline</span> the transfer on mobile.
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'qr' | 'invite')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="qr" className="gap-2">
                  <QrCode className="h-4 w-4" />
                  QR code
                </TabsTrigger>
                <TabsTrigger value="invite" className="gap-2">
                  <Mail className="h-4 w-4" />
                  Invitation
                </TabsTrigger>
              </TabsList>

              {/* ------------------ QR TAB ------------------ */}
              <TabsContent value="qr" className="mt-4 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Message (optional)</label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note (e.g., unit number, closing date, next steps)…"
                    rows={3}
                  />
                </div>

                {/* ✅ same tab, mid-card, padded */}
                <div className="rounded-lg border p-4">
                  {!qrPayload ? (
                    <div className="text-muted-foreground space-y-2 text-sm">
                      <div className="text-foreground font-medium">Generate a handover QR</div>
                      <div>
                        The buyer opens HomeVein → scans the QR → signs in by phone → then accepts/rejects the transfer.
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="text-sm font-medium">Handover code</div>
                          <div className="font-mono text-sm">{handoverCode ?? '—'}</div>
                          {expiresAt ? (
                            <div className="text-muted-foreground text-xs">
                              Expires: {new Date(expiresAt * 1000).toLocaleString()}
                            </div>
                          ) : null}
                        </div>

                        {handoverCode ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopy(handoverCode)}
                            className="gap-2"
                          >
                            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            {copied ? 'Copied' : 'Copy'}
                          </Button>
                        ) : null}
                      </div>

                      {/* ✅ QR display area (centered) */}
                      <div className="flex items-center justify-center rounded-lg border bg-white p-6">
                        <PropertyHandoverQr payload={qrPayload} />
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* ------------------ INVITE TAB ------------------ */}
              <TabsContent value="invite" className="mt-4 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Buyer email</label>
                  <div className="relative">
                    <Mail className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 h-4 w-4" />
                    <Input
                      value={buyerEmail}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                      placeholder="buyer@example.com"
                      className="pl-9"
                      autoFocus={activeTab === 'invite'}
                    />
                  </div>
                  {!buyerEmail ? (
                    <p className="text-muted-foreground text-xs">
                      We’ll email the buyer a link / code to claim the transfer in the app.
                    </p>
                  ) : !emailOk ? (
                    <p className="text-destructive text-xs">Please enter a valid email.</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Message (optional)</label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note for the buyer (e.g., unit number, closing date, next steps)…"
                    rows={4}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <Separator />

            {/* Warning */}
            <div className="flex gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />
              <div className="space-y-1">
                <div className="text-sm font-medium">Before you continue</div>
                <div className="text-muted-foreground text-xs">
                  Once the buyer accepts, <span className="font-medium">ownership will transfer</span> and your access
                  may change depending on your role rules.
                </div>
              </div>
            </div>

            {/* ✅ store-driven error */}
            {error ? (
              <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-lg border p-3 text-sm whitespace-pre-wrap">
                {error}
              </div>
            ) : null}
          </div>
          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={close} disabled={busy}>
              Cancel
            </Button>

            {activeTab === 'qr' ? (
              <Button type="button" onClick={handleGenerateQr} disabled={generatingQr || !propertyId}>
                {generatingQr ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span>
                    Generating…
                  </span>
                ) : qrPayload ? (
                  'Regenerate QR'
                ) : (
                  'Generate QR'
                )}
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmitInvite} disabled={!canSubmitInvite}>
                {submittingInvite ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span>
                    Sending invite…
                  </span>
                ) : (
                  'Send invite'
                )}
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
